import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useNavigation } from '../../contexts/NavigationContext';
import {
  Users,
  BookOpen,
  FolderOpen,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Mail,
  Link2,
  FileText,
  UserPlus,
  UserX,
  ClipboardList,
  AlertTriangle,
  FileWarning,
  Clock,
  LucideIcon,
} from 'lucide-react';
import { Spinner } from '../ui/Spinner';
import { Sparkline } from '../ui/Sparkline';
import { toast } from '../ui/Toaster';
import { bucketByWeek, accumulate, trendDelta, WEEKS_WINDOW, getWindow } from '../../lib/timeseries';

interface Stats {
  totalStudents: number;
  totalDocentes: number;
  totalAdmins: number;
  inactiveStudents: number;
  activeCourses: number;
  inactiveCourses: number;
  totalModules: number;
  averageAttendance: number;
  averageGrade: number;
  // Cards "requiere tu atención"
  bimPendientes: number;
  bimEnRevision: number;
  bimPublicadas: number;
  bimAtrasadas: number;     // en_revision desde hace > 5 días sin publicar
  alumnosEnRiesgo: number;  // promedio < 4.0 OR sin entrega 14 días
  tareasVencidas: number;   // todo_tasks con due_date pasado y no todos completaron
  tareasPendientes: number; // todo_tasks aún en progreso (sin vencer)
  docentesInactivos: number;// docentes activos sin acción en > 14 días
  totalDocentesEval: number;// docentes activos considerados en el cálculo
  // Series semanales (8 semanas) para sparklines
  attendanceSeries: number[];
  gradesSeries:     number[];
  studentsSeries:   number[];
  modulesSeries:    number[];
}

interface Activity {
  id: string;
  icon: LucideIcon;
  tone: string;
  text: string;
  time: string;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Recién';
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Hace ${days} d`;
  return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

export function AdminDashboard() {
  const { navigateTo } = useNavigation();
  const [stats, setStats] = useState<Stats>({
    totalStudents: 0,
    totalDocentes: 0,
    totalAdmins: 0,
    inactiveStudents: 0,
    activeCourses: 0,
    inactiveCourses: 0,
    totalModules: 0,
    averageAttendance: 0,
    averageGrade: 0,
    bimPendientes: 0,
    bimEnRevision: 0,
    bimPublicadas: 0,
    bimAtrasadas: 0,
    alumnosEnRiesgo: 0,
    tareasVencidas: 0,
    tareasPendientes: 0,
    docentesInactivos: 0,
    totalDocentesEval: 0,
    attendanceSeries: [],
    gradesSeries: [],
    studentsSeries: [],
    modulesSeries: [],
  });
  const [activity, setActivity] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Calculamos en paralelo TODO lo que se puede. Cada Promise.all entra
      // a un grupo aparte para no exceder el límite implícito de PostgREST
      // y para que los errores de uno no maten todo (catch individuales).
      const [
        studentsActiveRes, studentsInactiveRes,
        docentesListRes, adminsRes,
        coursesActiveRes, coursesInactiveRes,
        modulesRes, attendanceRes, enrollmentsRes,
        bimRes, bimComentariosRes,
        courseEvalsRes,
        tareasRes, recipientsRes,
        recentUsers, recentCourses, recentMaterials,
      ] = await Promise.all([
        // Alumnos activos: ahora traemos created_at para construir el sparkline
        supabase.from('profiles').select('id, created_at').eq('role', 'alumno').eq('active', true),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'alumno').eq('active', false),
        supabase.from('profiles').select('id').eq('role', 'docente').eq('active', true),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'admin').eq('active', true),
        supabase.from('courses').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('courses').select('id', { count: 'exact', head: true }).neq('status', 'active'),
        // Módulos: course_id + end_date para asistencia esperada · created_at para sparkline
        supabase.from('modules').select('id, course_id, end_date, created_at'),
        // Asistencia: solo guarda registros con present=true (ver markAttendance).
        // Por eso el % real se calcula contra módulos pasados × inscripciones.
        supabase.from('attendance').select('module_id, alumno_id, present, marked_at'),
        // course_enrollments para saber cuántos alumnos hay por curso (asistencia esperada)
        supabase.from('course_enrollments').select('course_id, alumno_id').eq('status', 'active'),
        supabase.from('bim_entregas').select('status, updated_at, uploaded_by'),
        supabase.from('bim_comentarios').select('author_id, updated_at'),
        // Promedio real: course_evaluations.final_average (la tabla `grades` está sin uso)
        supabase.from('course_evaluations').select('alumno_id, final_average, updated_at'),
        supabase.from('todo_tasks').select('id, due_date, created_by, created_at'),
        supabase.from('todo_task_recipients').select('task_id, completed'),
        supabase
          .from('profiles')
          .select('id, full_name, created_at')
          .order('created_at', { ascending: false })
          .limit(3),
        supabase
          .from('courses')
          .select('id, name, updated_at')
          .order('updated_at', { ascending: false })
          .limit(3),
        supabase
          .from('library_materials')
          .select('id, title, created_at')
          .order('created_at', { ascending: false })
          .limit(3),
      ]);

      // ─── Asistencia real: presentes / esperados (módulos pasados × inscripciones) ──
      // La app solo inserta registros cuando alguien marca presente, por eso no
      // podemos derivar "esperados" del propio attendance.
      const nowMsAtt = Date.now();
      const enrollmentsByCourse = new Map<string, string[]>();
      for (const e of (enrollmentsRes.data ?? [])) {
        const arr = enrollmentsByCourse.get(e.course_id) ?? [];
        arr.push(e.alumno_id);
        enrollmentsByCourse.set(e.course_id, arr);
      }
      const presentSet = new Set<string>();
      for (const a of (attendanceRes.data ?? [])) {
        if (a.present) presentSet.add(`${a.module_id}|${a.alumno_id}`);
      }
      let attExpected = 0, attReal = 0;
      for (const m of (modulesRes.data ?? [])) {
        if (!m.end_date) continue;
        const endMs = new Date(m.end_date).getTime();
        if (Number.isNaN(endMs) || endMs > nowMsAtt) continue;
        const enrolled = enrollmentsByCourse.get(m.course_id) ?? [];
        for (const aId of enrolled) {
          attExpected++;
          if (presentSet.has(`${m.id}|${aId}`)) attReal++;
        }
      }
      const avgAttendance = attExpected > 0 ? (attReal / attExpected) * 100 : 0;

      // ─── Promedio real: course_evaluations.final_average (>0) ────────
      const evalRows = courseEvalsRes.data ?? [];
      const validFinals = evalRows
        .map((e: any) => typeof e.final_average === 'string' ? parseFloat(e.final_average) : e.final_average)
        .filter((v: number) => v && v > 0);
      const avgGrade = validFinals.length > 0
        ? validFinals.reduce((a: number, b: number) => a + b, 0) / validFinals.length
        : 0;

      // ─── Series semanales (8 semanas) para sparklines ────────────
      // Asistencia: cantidad de registros (presents) por semana — refleja actividad de marcado.
      // Nota: el % real ya se calcula arriba contra módulos pasados; el sparkline
      // muestra "intensidad de asistencia" como señal temporal.
      const attendanceSeries = bucketByWeek(
        attendanceRes.data ?? [],
        (a: any) => a.marked_at,
        (rows) => rows.filter((r: any) => r.present).length,
      );
      // Promedio: media de course_evaluations.final_average por semana (cuando se actualiza)
      const gradesSeries = bucketByWeek(
        evalRows,
        (e: any) => e.updated_at,
        (rows) => {
          const valid = rows
            .map((r: any) => typeof r.final_average === 'string' ? parseFloat(r.final_average) : r.final_average)
            .filter((v: number) => v && v > 0);
          if (valid.length === 0) return 0;
          return Math.round((valid.reduce((a: number, b: number) => a + b, 0) / valid.length) * 10) / 10;
        },
      );
      // Alumnos: total acumulado de alumnos activos al cierre de cada semana.
      // Baseline = alumnos creados antes del inicio del periodo.
      const { startMs: windowStartMs } = getWindow();
      const studentsRows = studentsActiveRes.data ?? [];
      const studentsBaseline = studentsRows.filter(
        (s: any) => s.created_at && new Date(s.created_at).getTime() < windowStartMs,
      ).length;
      const studentsDeltas = bucketByWeek(
        studentsRows,
        (s: any) => s.created_at,
        (rows) => rows.length,
      );
      const studentsSeries = accumulate(studentsDeltas, studentsBaseline);
      // Módulos creados por semana (incremento, no acumulado — más expresivo)
      const modulesSeries = bucketByWeek(
        modulesRes.data ?? [],
        (m: any) => m.created_at,
        (rows) => rows.length,
      );

      // ─── Métricas BIM ─────────────────────────────────────────────
      const bimRows = bimRes.data ?? [];
      const nowMs = Date.now();
      const fiveDaysAgoMs = nowMs - 5 * 24 * 60 * 60 * 1000;
      const fourteenDaysAgoMs = nowMs - 14 * 24 * 60 * 60 * 1000;
      let bimPendientes = 0, bimEnRevision = 0, bimPublicadas = 0, bimAtrasadas = 0;
      for (const r of bimRows) {
        if (r.status === 'pendiente')   bimPendientes++;
        else if (r.status === 'en_revision') {
          bimEnRevision++;
          // "Atrasada" = en_revision pero el docente la dejó sin tocar > 5 días
          if (r.updated_at && new Date(r.updated_at).getTime() < fiveDaysAgoMs) bimAtrasadas++;
        }
        else if (r.status === 'corregido')   bimPublicadas++;
      }

      // ─── Alumnos en riesgo: final_average < 4.0 (excluyendo 0 = sin notas) ──
      const evals = courseEvalsRes.data ?? [];
      const enRiesgoIds = new Set<string>();
      for (const e of evals) {
        const avg = typeof e.final_average === 'string' ? parseFloat(e.final_average) : e.final_average;
        if (avg && avg > 0 && avg < 4.0) enRiesgoIds.add(e.alumno_id);
      }

      // ─── Tareas: vencidas vs pendientes (mismo derivado que TareasPendientesPage) ──
      const recipientsByTask = new Map<string, { total: number; completados: number }>();
      (recipientsRes.data ?? []).forEach((r: any) => {
        const cur = recipientsByTask.get(r.task_id) ?? { total: 0, completados: 0 };
        cur.total++;
        if (r.completed) cur.completados++;
        recipientsByTask.set(r.task_id, cur);
      });
      let tareasVencidas = 0, tareasPendientes = 0;
      for (const t of (tareasRes.data ?? [])) {
        const s = recipientsByTask.get(t.id) ?? { total: 0, completados: 0 };
        const isCompleted = s.total > 0 && s.completados >= s.total;
        if (isCompleted) continue;
        const dueMs = t.due_date ? new Date(t.due_date).getTime() : null;
        if (dueMs !== null && dueMs < nowMs) tareasVencidas++;
        else                                  tareasPendientes++;
      }

      // ─── Docentes inactivos: sin acción en > 14 días ──────────────
      // Acción = subir entrega BIM, dejar comentario BIM, crear tarea TODO
      const ultimaActDocente = new Map<string, number>();
      const bumpAct = (uid: string | null | undefined, iso: string | null | undefined) => {
        if (!uid || !iso) return;
        const ms = new Date(iso).getTime();
        const cur = ultimaActDocente.get(uid) ?? 0;
        if (ms > cur) ultimaActDocente.set(uid, ms);
      };
      (bimRes.data ?? []).forEach((b: any) => bumpAct(b.uploaded_by, b.updated_at));
      (bimComentariosRes.data ?? []).forEach((b: any) => bumpAct(b.author_id, b.updated_at));
      (tareasRes.data ?? []).forEach((t: any) => bumpAct(t.created_by, t.created_at));

      const docentesList = docentesListRes.data ?? [];
      let docentesInactivos = 0;
      for (const d of docentesList) {
        const lastMs = ultimaActDocente.get(d.id) ?? 0;
        if (lastMs < fourteenDaysAgoMs) docentesInactivos++;
      }

      setStats({
        totalStudents:  studentsRows.length,
        totalDocentes:  docentesList.length,
        totalAdmins:    adminsRes.count || 0,
        inactiveStudents: studentsInactiveRes.count || 0,
        activeCourses:    coursesActiveRes.count || 0,
        inactiveCourses:  coursesInactiveRes.count || 0,
        totalModules:     (modulesRes.data ?? []).length,
        averageAttendance: Math.round(avgAttendance),
        averageGrade:      Math.round(avgGrade * 10) / 10,
        bimPendientes,
        bimEnRevision,
        bimPublicadas,
        bimAtrasadas,
        alumnosEnRiesgo: enRiesgoIds.size,
        tareasVencidas,
        tareasPendientes,
        docentesInactivos,
        totalDocentesEval: docentesList.length,
        attendanceSeries,
        gradesSeries,
        studentsSeries,
        modulesSeries,
      });

      const feed: Activity[] = [];
      (recentUsers.data ?? []).forEach((u) =>
        feed.push({
          id: `user-${u.id}`,
          icon: UserPlus,
          tone: 'bg-cubus-cyan/20 text-cubus-cyan',
          text: `Nuevo alumno: ${u.full_name ?? 'sin nombre'}`,
          time: timeAgo(u.created_at),
        }),
      );
      (recentCourses.data ?? []).forEach((c) =>
        feed.push({
          id: `course-${c.id}`,
          icon: BookOpen,
          tone: 'bg-emerald-400/20 text-emerald-400',
          text: `Curso actualizado: ${c.name}`,
          time: timeAgo(c.updated_at ?? new Date().toISOString()),
        }),
      );
      (recentMaterials.data ?? []).forEach((m) =>
        feed.push({
          id: `mat-${m.id}`,
          icon: FolderOpen,
          tone: 'bg-cubus-violet/20 text-cubus-violet',
          text: `Material: ${m.title}`,
          time: timeAgo(m.created_at),
        }),
      );
      feed.sort((a, b) => (a.time > b.time ? -1 : 1));
      setActivity(feed.slice(0, 6));
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('No se pudieron cargar las métricas');
    } finally {
      setLoading(false);
    }
  };

  type StatCard = {
    title: string;
    value: string | number;
    icon: LucideIcon;
    gradient: string;
    accent: string;
    hint?: string;
    /** Serie semanal (8 buckets) para sparkline */
    series?: number[];
    /** Etiqueta accesible del sparkline */
    seriesLabel?: string;
    /** Si la métrica "más alto = mejor" — para colorear el delta */
    higherIsBetter?: boolean;
  };

  // Construimos los hints con contexto: en vez de "Usuarios activos" mostramos
  // datos comparativos útiles para el admin (ej. "+ 3 inactivos", "↓ 70%").
  const studentsHint = stats.inactiveStudents > 0
    ? `${stats.totalDocentes} docentes · ${stats.inactiveStudents} inactivo${stats.inactiveStudents === 1 ? '' : 's'}`
    : `${stats.totalDocentes} docentes activos`;

  const coursesHint = stats.inactiveCourses > 0
    ? `${stats.inactiveCourses} no activo${stats.inactiveCourses === 1 ? '' : 's'}`
    : 'Todos activos';

  const attendanceHint = stats.averageAttendance >= 80
    ? '✓ Excelente'
    : stats.averageAttendance >= 70
      ? 'Aceptable'
      : '⚠ Requiere atención';

  const gradeHint = stats.averageGrade >= 5.5
    ? '✓ Por encima de 5.5'
    : stats.averageGrade >= 4.0
      ? 'Sobre la nota mínima'
      : stats.averageGrade > 0
        ? '⚠ Debajo del mínimo'
        : 'Sin notas aún';

  const statCards: StatCard[] = [
    {
      title: 'Alumnos Activos',
      value: stats.totalStudents,
      icon: Users,
      gradient: 'from-cubus-cyan/20 to-cubus-cyan/5',
      accent: 'text-cubus-cyan',
      hint: studentsHint,
      series: stats.studentsSeries,
      seriesLabel: 'Crecimiento de alumnos en las últimas 8 semanas',
      higherIsBetter: true,
    },
    {
      title: 'Cursos Activos',
      value: stats.activeCourses,
      icon: BookOpen,
      gradient: 'from-emerald-400/20 to-emerald-400/5',
      accent: 'text-emerald-400',
      hint: coursesHint,
    },
    {
      title: 'Total Módulos',
      value: stats.totalModules,
      icon: FolderOpen,
      gradient: 'from-cubus-violet/25 to-cubus-violet/5',
      accent: 'text-cubus-violet',
      hint: stats.activeCourses > 0
        ? `~${Math.round(stats.totalModules / Math.max(1, stats.activeCourses))} por curso`
        : undefined,
      series: stats.modulesSeries,
      seriesLabel: 'Módulos creados por semana en las últimas 8 semanas',
      higherIsBetter: true,
    },
    {
      title: 'Asistencia',
      value: `${stats.averageAttendance}%`,
      icon: CheckCircle,
      gradient: 'from-cubus-lime/25 to-cubus-lime/5',
      accent: 'text-cubus-lime',
      hint: attendanceHint,
      series: stats.attendanceSeries,
      seriesLabel: '% de asistencia en las últimas 8 semanas',
      higherIsBetter: true,
    },
    {
      title: 'Promedio',
      value: stats.averageGrade > 0 ? stats.averageGrade.toFixed(1) : '—',
      icon: TrendingUp,
      gradient: 'from-orange-400/20 to-orange-400/5',
      accent: 'text-orange-400',
      hint: gradeHint,
      series: stats.gradesSeries,
      seriesLabel: 'Promedio de notas de las últimas 8 semanas',
      higherIsBetter: true,
    },
  ];

  type QuickAction = { icon: LucideIcon; label: string; view: string; tone: string };
  const quickActions: QuickAction[] = [
    { icon: BookOpen, label: 'Gestionar Cursos', view: 'courses', tone: 'text-emerald-400' },
    { icon: Users, label: 'Gestionar Usuarios', view: 'users', tone: 'text-cubus-cyan' },
    { icon: FolderOpen, label: 'Biblioteca', view: 'library', tone: 'text-cubus-violet' },
    { icon: Mail, label: 'Envío de Correos', view: 'emails', tone: 'text-cubus-lime' },
    { icon: Link2, label: 'Gestión de Links', view: 'links', tone: 'text-orange-400' },
    { icon: FileText, label: 'Ver Reportes', view: 'reports', tone: 'text-pink-400' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-slide-up p-4 sm:p-6 lg:p-8 overflow-y-auto">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl glass-elevated p-8 border-gradient">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-cubus-lime/10 rounded-full blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="chip chip-lime mb-3">
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              <span>Panel de Administración</span>
            </div>
            <h1 className="text-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">
              Vista general del <span className="text-lime-glow">sistema</span>
            </h1>
            <p className="text-white/60 max-w-xl">
              Monitorea la actividad educativa, gestiona cursos, usuarios y contenido desde un solo lugar.
            </p>
          </div>
          <button onClick={() => navigateTo('reports')} className="btn-lime self-start md:self-auto">
            <span>Ver reportes</span>
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* ── Requiere tu atención ─────────────────────────────────── */}
      {(stats.bimPendientes > 0 || stats.bimEnRevision > 0 || stats.bimAtrasadas > 0
        || stats.alumnosEnRiesgo > 0 || stats.tareasVencidas > 0 || stats.docentesInactivos > 0) && (
        <div>
          <h2 className="text-sm font-medium text-white/50 uppercase tracking-widest mb-4 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            Requiere tu atención
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Card Entregas BIM */}
            <button
              onClick={() => navigateTo('bim-entregas')}
              className="text-left relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover bg-gradient-to-br from-cubus-lime/15 to-cubus-lime/5 border border-cubus-lime/20 hover:border-cubus-lime/40 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-11 h-11 rounded-xl bg-cubus-lime/15 border border-cubus-lime/30 flex items-center justify-center text-cubus-lime">
                  <FileWarning className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/50 font-medium uppercase tracking-wider mb-1">
                    Entregas BIM
                  </p>
                  <p className="text-display text-3xl font-bold text-white tracking-tight">
                    {stats.bimPendientes + stats.bimEnRevision}
                  </p>
                  <div className="text-[11px] text-white/60 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {stats.bimPendientes > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                        {stats.bimPendientes} pendiente{stats.bimPendientes === 1 ? '' : 's'}
                      </span>
                    )}
                    {stats.bimEnRevision > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        {stats.bimEnRevision} en revisión
                      </span>
                    )}
                    {stats.bimPublicadas > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-cubus-lime" />
                        {stats.bimPublicadas} publicada{stats.bimPublicadas === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  {stats.bimAtrasadas > 0 && (
                    <p className="text-[11px] text-amber-400 mt-1.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {stats.bimAtrasadas} atrasada{stats.bimAtrasadas === 1 ? '' : 's'} (sin tocar +5 días)
                    </p>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-cubus-lime group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>

            {/* Card Alumnos en riesgo */}
            <button
              onClick={() => navigateTo('alumnos-riesgo')}
              className={`text-left relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover bg-gradient-to-br border transition-all group ${
                stats.alumnosEnRiesgo > 0
                  ? 'from-rose-500/15 to-rose-500/5 border-rose-500/25 hover:border-rose-500/45'
                  : 'from-emerald-400/10 to-emerald-400/5 border-emerald-400/20 hover:border-emerald-400/40'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center ${
                  stats.alumnosEnRiesgo > 0
                    ? 'bg-rose-500/15 border-rose-500/30 text-rose-400'
                    : 'bg-emerald-400/15 border-emerald-400/30 text-emerald-400'
                }`}>
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/50 font-medium uppercase tracking-wider mb-1">
                    Alumnos en riesgo
                  </p>
                  <p className="text-display text-3xl font-bold text-white tracking-tight">
                    {stats.alumnosEnRiesgo}
                  </p>
                  <p className="text-[11px] text-white/60 mt-2">
                    {stats.alumnosEnRiesgo === 0
                      ? '✓ Todos los alumnos están sobre 4.0 de promedio'
                      : `Con promedio final < 4.0 · de ${stats.totalStudents} activos`}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-cubus-lime group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>

            {/* Card Tareas vencidas */}
            <button
              onClick={() => navigateTo('tareas-pendientes')}
              className={`text-left relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover bg-gradient-to-br border transition-all group ${
                stats.tareasVencidas > 0
                  ? 'from-amber-500/15 to-amber-500/5 border-amber-500/25 hover:border-amber-500/45'
                  : 'from-emerald-400/10 to-emerald-400/5 border-emerald-400/20 hover:border-emerald-400/40'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center ${
                  stats.tareasVencidas > 0
                    ? 'bg-amber-500/15 border-amber-500/30 text-amber-400'
                    : 'bg-emerald-400/15 border-emerald-400/30 text-emerald-400'
                }`}>
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/50 font-medium uppercase tracking-wider mb-1">
                    Tareas vencidas
                  </p>
                  <p className="text-display text-3xl font-bold text-white tracking-tight">
                    {stats.tareasVencidas}
                  </p>
                  <div className="text-[11px] text-white/60 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {stats.tareasVencidas > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                        {stats.tareasVencidas} sin cerrar
                      </span>
                    )}
                    {stats.tareasPendientes > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        {stats.tareasPendientes} en curso
                      </span>
                    )}
                    {stats.tareasVencidas === 0 && stats.tareasPendientes === 0 && (
                      <span className="text-emerald-400">✓ Sin tareas activas vencidas</span>
                    )}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-cubus-lime group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>

            {/* Card Docentes inactivos */}
            <button
              onClick={() => navigateTo('actividad-docente')}
              className={`text-left relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover bg-gradient-to-br border transition-all group ${
                stats.docentesInactivos > 0
                  ? 'from-cubus-violet/20 to-cubus-violet/5 border-cubus-violet/25 hover:border-cubus-violet/45'
                  : 'from-emerald-400/10 to-emerald-400/5 border-emerald-400/20 hover:border-emerald-400/40'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center ${
                  stats.docentesInactivos > 0
                    ? 'bg-cubus-violet/15 border-cubus-violet/30 text-cubus-violet'
                    : 'bg-emerald-400/15 border-emerald-400/30 text-emerald-400'
                }`}>
                  <UserX className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/50 font-medium uppercase tracking-wider mb-1">
                    Docentes inactivos
                  </p>
                  <p className="text-display text-3xl font-bold text-white tracking-tight">
                    {stats.docentesInactivos}
                  </p>
                  <p className="text-[11px] text-white/60 mt-2">
                    {stats.totalDocentesEval === 0
                      ? 'No hay docentes activos en el campus'
                      : stats.docentesInactivos === 0
                        ? '✓ Todos activos en los últimos 14 días'
                        : `Sin acción +14 días · de ${stats.totalDocentesEval} activos`}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-cubus-lime group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div>
        <h2 className="text-sm font-medium text-white/50 uppercase tracking-widest mb-4">
          Indicadores clave
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {statCards.map((card, index) => {
            // Calcular delta % entre la última semana y el promedio de las anteriores.
            // Solo lo mostramos si hay datos (>1 valor distinto de 0) — evita ruido en cards vacías.
            const series = card.series ?? [];
            const hasMeaningfulSeries = series.length >= 2 && series.some((v) => v > 0);
            const delta = hasMeaningfulSeries ? trendDelta(series) : null;
            const showDelta = delta !== null && Math.abs(delta) >= 1;
            const isUp = (delta ?? 0) > 0;
            const deltaIsGood = card.higherIsBetter !== false ? isUp : !isUp;
            const deltaColor = !showDelta
              ? 'text-white/30'
              : deltaIsGood
                ? 'text-cubus-lime'
                : 'text-rose-400';
            return (
              <div
                key={index}
                className={`relative overflow-hidden glass-surface rounded-2xl p-5 glass-hover group bg-gradient-to-br ${card.gradient}`}
                style={{ animationDelay: `${index * 60}ms` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div
                    className={`relative w-11 h-11 rounded-xl bg-white/[0.06] backdrop-blur-sm border border-white/10 flex items-center justify-center ${card.accent}`}
                  >
                    <card.icon className="w-5 h-5" />
                  </div>
                  {showDelta && (
                    <span
                      className={`text-[11px] font-semibold tabular-nums ${deltaColor}`}
                      title={`Cambio vs promedio del periodo anterior (${WEEKS_WINDOW} semanas)`}
                    >
                      {isUp ? '↑' : '↓'} {Math.abs(delta!).toFixed(0)}%
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-xs text-white/50 font-medium mb-1">{card.title}</p>
                  <p className="text-display text-3xl font-bold text-white tracking-tight">
                    {card.value}
                  </p>
                  {card.hint && <p className="text-[11px] text-white/40 mt-1">{card.hint}</p>}
                </div>
                {hasMeaningfulSeries && (
                  <div className={`mt-3 -mx-1 ${card.accent}`}>
                    <Sparkline
                      values={series}
                      ariaLabel={card.seriesLabel}
                      height={28}
                    />
                  </div>
                )}
                <div
                  className={`absolute -bottom-px left-4 right-4 h-px bg-gradient-to-r from-transparent ${card.accent.replace(
                    'text-',
                    'via-',
                  )}/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity`}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Two-col */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-surface rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-white text-display">Acciones rápidas</h3>
            <span className="chip">
              <span className="w-1.5 h-1.5 rounded-full bg-cubus-lime animate-pulse" />
              <span>6 disponibles</span>
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {quickActions.map((action) => (
              <button
                key={action.view}
                onClick={() => navigateTo(action.view)}
                className="group flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.06] hover:border-cubus-lime/40 transition-all focus-lime text-left"
              >
                <div
                  className={`w-10 h-10 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center ${action.tone} group-hover:scale-110 transition-transform`}
                >
                  <action.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">{action.label}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-cubus-lime group-hover:translate-x-0.5 transition-all" />
              </button>
            ))}
          </div>
        </div>

        <div className="glass-surface rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-white text-display">Actividad reciente</h3>
            <button
              onClick={() => navigateTo('reports')}
              className="text-xs text-muted hover:text-cubus-lime transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cubus-lime rounded"
            >
              Ver todo
            </button>
          </div>
          {activity.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
                <ClipboardList className="w-5 h-5 text-faint" aria-hidden="true" />
              </div>
              <p className="text-sm text-muted">Aún no hay actividad reciente.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {activity.map((item) => (
                <li key={item.id} className="flex items-start gap-3">
                  <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${item.tone}`}>
                    <item.icon className="w-4 h-4" aria-hidden="true" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/90 text-sm truncate">{item.text}</p>
                    <p className="text-faint text-xs mt-0.5">{item.time}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
